<template>
  <div v-if="!isDisabled && isOpened">
    <div class="subscription-p">
      <div class="subscription-p-inner">
        <button
          aria-label="Close Subscription Popup"
          class="button button--close"
          type="button"
          @click="closeSubscriptionPopup"
        >
          <Icon name="fa6-solid:xmark" />
          <div class="sr-only">Close Subscription Popup</div>
        </button>
        <div v-if="status === 'sent'" class="subscription-p-content">
          <transition appear name="fade">
            <div class="subscription-p-error">
              <h2>Thank you!</h2>
              <p class="note success">
                {{ messages.success }}
              </p>
              <FormKit
                :input-class="['button button--success button--xl']"
                class="subscription-p__button"
                type="button"
                @click="closeSubscriptionPopup"
              >
                <span> Close </span>
              </FormKit>
            </div>
          </transition>
        </div>
        <div v-else class="subscription-p-content">
          <h2 v-if="title">
            {{ title }}
          </h2>
          <p v-if="description">
            {{ description }}
          </p>
          <FilterByTopics
            v-if="containsTopicsField"
            v-model="topics"
            :is-multi-select="true"
            :label="topicsLabel"
            :show-selections="true"
            placeholder="Please select"
          />
          <FilterByAudiences
            v-if="containsAudiencesField"
            v-model="audiences"
            :is-multi-select="true"
            :label="audiencesLabel"
            :show-selections="true"
            placeholder="Please select"
          />
          <div v-if="containsStatesField" class="filter">
            <FormkitSingleSelect
              v-model="selectedState"
              :label="statesLabel"
              :options="computedStates"
              :show-reset="false"
              :show-selections="true"
              placeholder="Please select"
            />
          </div>
          <FormKit
            :id="formId"
            v-slot="{ disabled }"
            v-model="values"
            :actions="false"
            name="subscription-popup-form"
            type="form"
            @submit="sendAndSetCookie"
          >
            <!-- @vue-expect-error model type errors -->
            <FormKit
              v-if="containsTopicsField && topics.length"
              key="topics"
              v-model="topics"
              name="topics"
              type="hidden"
            />
            <!-- @vue-expect-error model type errors -->
            <FormKit
              v-if="containsAudiencesField && audiences.length"
              key="audiences"
              v-model="audiences"
              name="audiences"
              type="hidden"
            />
            <FormKit
              v-if="containsStatesField && selectedState.length"
              key="state"
              v-model="selectedState[0]"
              name="MMERGE5"
              type="hidden"
            />
            <FormKit
              v-if="containsNameField"
              key="name"
              :label="nameLabel"
              name="FNAME"
              placeholder="John Smith"
              type="text"
              validation="length:1,50"
            />
            <FormKit
              key="email"
              label="Email *"
              name="email"
              placeholder="name@example.com"
              type="email"
              validation="required|email|length:1,50"
            />
            <FormKit
              v-if="containsPostcodeField"
              key="postcode"
              :label="postcodeLabel"
              name="POSTCODE1"
              type="number"
              validation="length:4,4|number"
            />
            <transition
              name="form-messages"
              @after-enter="clearMessages"
              @before-enter="transitionBeforeEnter"
            >
              <div
                v-show="error"
                :id="'form_messages'"
                class="subscription-p__messages"
              >
                <p v-if="error" class="error">
                  {{ messages.error }}
                </p>
              </div>
            </transition>
            <FormKit
              :disabled="!!disabled"
              :input-class="['button button--success button--xl']"
              class="subscription-p__button"
              type="submit"
            >
              <span>
                {{ !!disabled ? 'Submitting...' : 'Submit' }}
              </span>
              <i class="icon icon--lg">
                <Icon name="fa6-solid:arrow-right" />
              </i>
            </FormKit>
            <div class="legend">* indicates mandatory field</div>
            <CaptchaPolicy />
          </FormKit>
        </div>
      </div>
    </div>
    <div class="subscription-p-trigger" @click="closeSubscriptionPopup" />
  </div>
</template>

<script setup lang="ts">
import type {
  BlockSubscriptionSettingsStoryblok,
  SubscriptionPopupStoryblok,
} from '~/types/storyblok'

const props = defineProps<{
  blok?: BlockSubscriptionSettingsStoryblok | SubscriptionPopupStoryblok
}>()
const formId = ref('subscription-form')
const { values, error, status, transitionBeforeEnter, send, clearMessages } =
  useForm('/api/forms/subscription', formId.value)

const isOpened = ref(false)
const messages = ref({
  error:
    "We're sorry, but there was a problem with sending your request. Please try again later",
  success: 'Your request was successfully sent.',
})
const topics = ref([])
const audiences = ref([])
const selectedState = ref([])
const showAfterDelay = ref()

const { fetchStates } = useStoryblokDataSources()
const { data: states } = await fetchStates()
const { subscriptionPopup } = storeToRefs(useConfigStore())
const { viewsCount } = storeToRefs(useViewsCountStore())
const { viewedTopics } = storeToRefs(useViewedTopicsStore())
const { viewedAudiences } = storeToRefs(useViewedAudiencesStore())

const globalTitle = computed(() => subscriptionPopup.value?.title)
const globalDescription = computed(() => subscriptionPopup.value?.description)
const globalDelay = computed(() => subscriptionPopup.value?.delay)
const globalFields = computed(() => subscriptionPopup.value?.fields || [])
const globalPages = computed(() => subscriptionPopup.value?.pages)
const globalShowOnScroll = computed(() => subscriptionPopup.value?.scroll)
const globalTopicsFieldLabel = computed(
  () => subscriptionPopup.value?.topics_label,
)
const globalAudiencesFieldLabel = computed(
  () => subscriptionPopup.value?.audiences_label,
)
const globalNameLabel = computed(() => subscriptionPopup.value?.name_label)
const globalPostcodeLabel = computed(
  () => subscriptionPopup.value?.postcode_label,
)
const globalStatesLabel = computed(() => subscriptionPopup.value?.states_label)
const title = computed(
  () => props.blok?.title || globalTitle.value || 'Subscribe',
)
const description = computed(
  () => props.blok?.description || globalDescription.value,
)
const computedStates = computed(() =>
  [...states.value].map((option) => ({
    label: option.name === 'National' ? 'Outside AU' : option.name,
    value: option.value === 'national' ? 'outside' : option.name,
  })),
)
const delay = computed(
  () => parseInt(props.blok?.delay || globalDelay.value) || 0,
)
const pages = computed(() =>
  props.blok && Object.keys(props.blok).length
    ? 0
    : parseInt(globalPages.value),
)
const showOnScroll = computed(() =>
  props.blok?.scroll === false
    ? false
    : props.blok?.scroll === true
      ? true
      : !!globalShowOnScroll.value,
)
const isDisabled = computed(() =>
  props.blok?.disabled ? true : !!subscriptionPopup.value?.disabled,
)
const cookie = useCookie<{ hideSubscriptionPopup?: boolean }>(
  'compass-subscription-params',
  {
    path: '/',
    maxAge: 60 * 60 * 24 * 90,
    sameSite: true,
  },
)
const isDisabledInCookies = computed(
  () => cookie.value?.hideSubscriptionPopup || false,
)
const containsNameField = computed(() =>
  props.blok?.name_field === 'hide'
    ? false
    : props.blok?.name_field === 'show'
      ? true
      : !!globalFields.value.includes('name'),
)
const containsTopicsField = computed(() =>
  props.blok?.topics_field === 'hide'
    ? false
    : props.blok?.topics_field === 'show'
      ? true
      : !!globalFields.value.includes('topics'),
)
const containsAudiencesField = computed(() =>
  props.blok?.audiences_field === 'hide'
    ? false
    : props.blok?.audiences_field === 'show'
      ? true
      : !!globalFields.value.includes('audiences'),
)
const containsPostcodeField = computed(() =>
  props.blok?.postcode_field === 'hide'
    ? false
    : props.blok?.postcode_field === 'show'
      ? true
      : !!globalFields.value.includes('postcode'),
)
const containsStatesField = computed(() =>
  props.blok?.states_field === 'hide'
    ? false
    : props.blok?.states_field === 'show'
      ? true
      : !!globalFields.value.includes('states'),
)
const topicsLabel = computed(
  () =>
    props.blok?.topics_field_label ||
    globalTopicsFieldLabel.value ||
    'What subject you are interested in?',
)
const audiencesLabel = computed(
  () =>
    props.blok?.audiences_field_label ||
    globalAudiencesFieldLabel.value ||
    'What audience you are interested in?',
)
const nameLabel = computed(
  () => props.blok?.name_field_label || globalNameLabel.value || 'Your name',
)
const postcodeLabel = computed(
  () =>
    props.blok?.postcode_field_label || globalPostcodeLabel.value || 'Postcode',
)
const statesLabel = computed(
  () =>
    props.blok?.states_field_label ||
    globalStatesLabel.value ||
    'What state are you from?',
)
const autoTopics = computed(() => {
  if (!viewedTopics.value) {
    return null
  }
  const topicsObject = viewedTopics.value
  return (
    Object.keys(topicsObject).sort(function (a, b) {
      return topicsObject[b] - topicsObject[a]
    }) || []
  )
})
const autoAudiences = computed(() => {
  if (!viewedAudiences.value) {
    return null
  }
  const audiencesObject = viewedAudiences.value
  return (
    Object.keys(audiencesObject).sort(function (a, b) {
      return audiencesObject[b] - audiencesObject[a]
    }) || []
  )
})

const { scrollY, handleScroll } = useScrollListener()
watch(
  () => scrollY,
  (newScroll) => {
    if (
      !isDisabled.value &&
      newScroll &&
      showOnScroll.value &&
      !isDisabledInCookies.value &&
      viewsCount.value >= pages.value
    ) {
      window.removeEventListener('scroll', handleScroll)
      showFormAfterDelay()
    }
  },
)

onMounted(() => {
  if (
    !isDisabled.value &&
    viewsCount.value >= pages.value &&
    !isDisabledInCookies.value &&
    !showOnScroll.value
  )
    showFormAfterDelay()
})

onBeforeUnmount(() => {
  isOpened.value = false
  document.body.classList.remove('subscription-is-open')
})

const closeSubscriptionPopup = () => {
  isOpened.value = false
  document.body.classList.remove('subscription-is-open')
  setFormCookie()
  clearMessages()
}
const showFormAfterDelay = () => {
  clearTimeout(showAfterDelay.value)
  showAfterDelay.value = setTimeout(() => {
    showForm()
  }, delay.value * 1000)
}
const showForm = () => {
  isOpened.value = true
  topics.value = autoTopics.value
  audiences.value = autoAudiences.value
  document.body.classList.toggle('subscription-is-open')
}
const setFormCookie = () => {
  cookie.value = { hideSubscriptionPopup: true }
}

function sendAndSetCookie(data) {
  clearMessages()
  try {
    send(data)
    setFormCookie()
  } catch (err) {
    error.value = err
    console.error(err)
  }
}
</script>

<style lang="less" scoped>
.subscription-p {
  .fixed();
  .animate-show();

  top: 50%;
  left: 50%;
  max-height: 100%;
  max-width: 100%;
  transform: translate(-50%, -50%);
  z-index: 10005;
  min-width: @320px;
  width: @420px;

  @media print {
    .hidden();
  }

  .subscription-p-inner {
    .relative();
    .shadow-popover();

    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    padding: @gutter-lg;
    background: fade(@color-body, 97%);

    @media @tablet {
      padding: @gutter-xl;
      overflow-y: visible;
    }

    button.button--close {
      .absolute();

      right: @gutter-xs / 2;
      top: @gutter-xs / 2;
    }

    .subscription-p__messages {
      transition: all 5s ease-in-out;

      .error {
        margin-top: @gutter;
        padding: @gutter-sm @gutter;
        background-color: @state-danger;
        color: @color-fill-normal;

        @media @desktop {
          margin-top: @gutter-xl;
        }
      }
    }

    .subscription-p__button {
      margin: @gutter 0;
    }

    .subscription-p-error {
      .subscription-p__button {
        margin: @gutter-lg 0 0;
      }
    }

    :deep(.filter) {
      margin-top: @gutter-lg;

      .multiselect__button-wrapper {
        background-color: @color-fill-normal;
        border: @1px solid @color-fill-muted;

        button:not(.reset) {
          padding: @gutter-xs @gutter @gutter-xs 0;
        }

        button.toggle .icon {
          transform: rotate(90deg);
        }
      }

      .boxgroup-wrapper {
        top: 100%;
        margin-top: -@1px;
        position: absolute;
        max-width: none;
        max-height: @220px;
        border-color: @color-fill-muted;
        border-top: none;

        .return {
          display: none;
        }
      }

      .label-filters {
        padding: 0;
        margin: 0;
        line-height: 2.8;
      }
    }
  }
}
.subscription-p-trigger {
  .fixed();
  .animate-show();

  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: fade(@color-fill-dark, 85%);
  z-index: 9999;
}
</style>
